/*------------------------------------------------------------------
External
------------------------------------------------------------------*/

import $ from 'cash-dom';
import dayjs from 'dayjs';
import tingle from 'tingle.js';
import Dropzone from 'dropzone';
import SignaturePad from 'signature_pad';
import Swal from 'sweetalert2/src/sweetalert2.js';
import Choices from 'choices.js';
import MatchHeight from '@tannerhodges/match-height';

/*------------------------------------------------------------------
Toast mixin for the Swal notifier
------------------------------------------------------------------*/

const UK_PORTAL_SITE = 'https://portal.spaceshipsrentals.co.uk';
// const UK_PORTAL_SITE = 'https://spaceships-mono-repo.ddev.site';
const AU_PORTAL_SITE = 'https://portal.spaceshipsrentals.com.au';


const Toast = Swal.mixin({
    toast: true,
    position: 'center-end',
    customClass: { container: 'toastify' },
    showConfirmButton: false,
    timer: 6000,
    timerProgressBar: true,
    didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer)
        toast.addEventListener('mouseleave', Swal.resumeTimer)
    }
})


/*------------------------------------------------------------------
Internal
------------------------------------------------------------------*/

import * as api from './api';
import * as log from './sentry';
import * as error from './errors';
import * as templates from './templates';

/*------------------------------------------------------------------
Options
------------------------------------------------------------------*/

window.has_unsaved_changes = false;
window.first_load = true;

window.dropzone_options = {
    uploadMultiple: false,
    acceptedFiles: '.jpg,.png,.jpeg,.pdf,.heic',
    parallelUploads: 1,
    autoProcessQueue: false,
    url: 'https://api.cloudinary.com/v1_1/rentalcarmanager/upload'
};

/*------------------------------------------------------------------
Functionality
------------------------------------------------------------------*/

/** 
* 
* Optional fees fetcher to get the currently selected addons and their quantities
* 
* @param {*} optional_fees 
* @returns array
*/
const getOptionalFees = (optional_fees = []) => {

    $('.cards__item__addons__item.added').each((index, item) => {
        optional_fees.push({
            id: parseInt($(item).attr('data-id')),
            qty: parseInt($(item).find('.js-addon-quantity').val()) || 1
        });
    });

    return optional_fees;
};

/**
*
* Sanitises a booking's existing extra fees (as returned by the API) into the
* optional fee shape used elsewhere in the portal, excluding the insurance fee
*
* @param {Array<{extrafeeid: number, qty: number, isinsurancefee: boolean}>} existing_extra_fees
* @returns {Array<{id: number, qty: number}>}
*/
const getExistingOptionalFees = (existing_extra_fees = []) => {
    let sanitised_fees = [];

    existing_extra_fees.forEach(fee => {
        if (!fee.isinsurancefee) {
            sanitised_fees.push({
                id: fee.extrafeeid,
                qty: fee.qty,
            })
        }
    });

    return sanitised_fees;
}

/**
* 
* Seleceted insurance fetcher to get the currently selected insurance item
* 
* @param {*} optional_fees 
* @returns array
*/
const getSelectedInsurance = () => {
    return parseInt($('.insurances__item.selected').attr('data-id'));
};


/**
* 
* Update the booking times of a booking
* 
* @param {string} updated_pickup_time 
* @param {string} updated_dropoff_time 
*/
const updateBookingTimes = (updated_pickup_time, updated_dropoff_time) => {

    // Assign our variables
    let booking_info = window.portal_data.bookinginfo[0];
    let existing_extra_fees = window.portal_data.extrafees;

    // Get our current dates
    let pick_up_date = booking_info.pickupdate;
    let drop_off_date = booking_info.dropoffdate;

    // Create our new date times
    let pick_up_date_time = `${new dayjs(pick_up_date, 'DD/MMM/YYYY').format('YYYY-MM-DD')} ${updated_pickup_time}`;
    let drop_off_date_time = `${new dayjs(drop_off_date, 'DD/MMM/YYYY').format('YYYY-MM-DD')} ${updated_dropoff_time}`;

    // Add a loading class to the button
    $('.js-open-booking-times-modal').addClass('busy');

    // Get the date of birth and expiration dates
    let date_of_birth = new dayjs(`${$('.js-date-of-birth-day-input').val()}/${$('.js-date-of-birth-month-input').val()}/${$('.js-date-of-birth-year-input').val()}`, 'DD/MM/YYYY').format('DD/MMM/YYYY');
    let date_of_expiration = new dayjs(`${$('.js-date-of-expiration-day-input').val()}/${$('.js-date-of-expiration-month-input').val()}/${$('.js-date-of-expiration-year-input').val()}`, 'DD/MM/YYYY').format('DD/MMM/YYYY');

    // Special actions to override the insurances
    // 22 - motorhome
    // 16 - motorhome
    // 1 - campervan
    // 12 - campervan
    // 14 - campervan
    // 17 - people carrier
    let defaultInsurance = 402;
    if (window.location.origin === AU_PORTAL_SITE) defaultInsurance = 294;
    if (window.location.origin === UK_PORTAL_SITE) defaultInsurance = 349;
    if (window.location.origin === UK_PORTAL_SITE) defaultInsurance = 349;
    if (window.location.origin === UK_PORTAL_SITE && booking_info.vehiclecategoryid === 22) defaultInsurance = 488;
    if (window.location.origin === UK_PORTAL_SITE && booking_info.vehiclecategoryid === 16) defaultInsurance = 488;
    if (window.location.origin === UK_PORTAL_SITE && booking_info.vehiclecategoryid === 25) defaultInsurance = 488;
    if (window.location.origin === UK_PORTAL_SITE && booking_info.vehiclecategoryid === 26) defaultInsurance = 488;
    if (window.location.origin === UK_PORTAL_SITE && booking_info.vehiclecategoryid === 1) defaultInsurance = 489;
    if (window.location.origin === UK_PORTAL_SITE && booking_info.vehiclecategoryid === 12) defaultInsurance = 489;
    if (window.location.origin === UK_PORTAL_SITE && booking_info.vehiclecategoryid === 14) defaultInsurance = 489;
    if (window.location.origin === UK_PORTAL_SITE && booking_info.vehiclecategoryid === 17) defaultInsurance = 490;
    if (window.location.origin === UK_PORTAL_SITE && booking_info.vehiclecategoryid === 18) defaultInsurance = 490;
    if (window.location.origin === UK_PORTAL_SITE && booking_info.vehiclecategoryid === 19) defaultInsurance = 490;

    // Get our exisiting addons only as they should not have changed for this booking
    let optional_fees = getExistingOptionalFees(existing_extra_fees);

    // Get our selected addons
    let selected_insurance = getSelectedInsurance();

    // Add a prefix to the remarks field depending on the country
    let remarks_prefix = (window.currency_name === 'NZD') ? '' : 'Expected Travel Distance: ';

    // Remove the prefix if it already exists in the fields' value
    if ($('.js-remarks-input').val().includes('Show-Around: ')) remarks_prefix = '';
    if ($('.js-remarks-input').val().includes('Expected Travel Distance: ')) remarks_prefix = '';
 
    // Send our data to RCM using the newly created update booking method
    api.update_booking_portal({
        reservation_reference: booking_info.reservationref,
        booking_type: 2,
        pick_up_date_time: pick_up_date_time,
        pick_up_location_id: booking_info.pickuplocationid,
        drop_off_date_time: drop_off_date_time,
        drop_off_location_id: booking_info.dropofflocationid,
        vehicle_category_id: booking_info.vehiclecategoryid,
        driver_age_id: booking_info.driverageid,
        insurance_id: selected_insurance,
        number_of_people_travelling: parseInt($('.js-travellers-input').val()),
        reservation_reference: booking_info.reservationref,
        customer_id: window.portal_data.customerinfo[0].customerid,
        first_name: $('.js-first-name-input').val(),
        last_name: $('.js-last-name-input').val(),
        email: $('.js-email-input').val(),
        date_of_birth: date_of_birth,
        phone: $('.js-phone-input').val(),
        mobile: $('.js-mobile-input').val(),
        license_number: $('.js-license-input').val(),
        license_expiry: date_of_expiration,
        license_issue_country: $('.js-license-country-input').val(),
        address: $('.js-address-input').val(),
        city: $('.js-city-input').val(),
        state: $('.js-state-input').val(),
        postcode: $('.js-postcode-input').val(),
        country: $('.js-country-select').val(),
        optional_fees: optional_fees,
        remarks: `${remarks_prefix}${$('.js-remarks-input').val()}` || '',
    })
        .then(json => {

            if (json.error) {
                // Remove the loading class to the button
                $('.js-open-booking-times-modal').removeClass('busy');

                error.showError(json.error);
            } else {

                // Fetch the updated data as they do not return the needed information fromt he previous call
                api.fetch_workflow_checklist({ reservation_reference: window.portal_data.bookinginfo[0].reservationref })
                    .then(json => {
                        window.portal_data = json.results;
                        populateDriverTab();
                        populateDriverTab();
                        populateAddonTab();
                        populatePaymentsTab();
                        populateSignaturesTab();
                        populateLicenseTab();

                        // Update the validity of the steps
                        update_step_validitiy();

                        // Show success message to the user
                        Toast.fire({
                            icon: 'success',
                            title: 'You have successfully updated your travel times'
                        });

                        // Remove the loading class to the button
                        $('.js-open-booking-times-modal').removeClass('busy');

                    });

            }

        }).catch(error => {

            // Show the error
            console.error(error);

            // Log any errors discretely
            log.error('API catch: make_booking', error);

        });
};


/**
* 
* Open up a modal with a form to update the booking times
* This will load the currently selected times into the form
* 
*/
const openBookingTimesModal = ($element) => {

    if ($element.hasClass('inactive')) {
        // Show success message to the user
        Toast.fire({
            icon: 'warning',
            title: 'Changing your booking time isn\'t possible <br><a target="_blank" href="https://faq.spaceshipsrentals.co.nz/content/change-of-pick-up-time-isn-t-allowed-or-the-option-isn-t-available-why-is-that">Click here to find out more</a>'
        });
        return false;
    }


    if (!$('.driverForm').is(':valid')) {
        // Show success message to the user if the form is not valid
        Swal.fire({
            title: 'Warning',
            html: `<p>Please fill out all fields on this page before making changes<br> to the pick-up/drop-off time.</p>`,
        })
        return false;
    }


    // Simplified RCM variables
    let booking_info = window.portal_data.bookinginfo[0];

    // Get the current booking times
    let pick_up_time = booking_info.pickuptime;
    let drop_off_time = booking_info.dropofftime;

    // Show the modal
    Swal.fire({
        customClass: { confirmButton: 'button button--green' },
        showCancelButton: true,
        denyButtonText: 'Cancel',
        confirmButtonText: 'Update',
        title: 'Update your travel times',
        html: templates.updated_booking_modal(pick_up_time, drop_off_time),
    }).then((result) => {

        // Only update the times if the user clicked confirm
        if (result.isConfirmed) {

            // Get our updated times
            let updated_pickup_time = $('.js-updated-pickup-time').val();
            let updated_dropoff_time = $('.js-updated-dropoff-time').val();

            // Make the call to RCM to update the booking times
            updateBookingTimes(updated_pickup_time, updated_dropoff_time);

        }
    });

    // Declare our choices options
    const options = {
        searchEnabled: false,
        itemSelectText: '',
        shouldSort: false,
        placeholder: true,
        allowHTML: true
    };


    setTimeout(() => {
        new Choices($('.js-updated-pickup-time')[0], options);
        new Choices($('.js-updated-dropoff-time')[0], options);
    }, 100);

};


// Reference to the current media stream
let camera_mode = 'environment';
let mediaStream = null;


// Prefer camera resolution nearest to 1280x720.
let constraints = {
    audio: false,
    video: {
        width: { ideal: 640 },
        height: { ideal: 480 },
        facingMode: 'environment'
    }
};


/**
* 
* Get the camera object for the user
* 
* @param {object} constraints 
*/
async function getMediaStream(constraints) {
    try {
        mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
        let video = document.getElementById('cam');
        video.srcObject = mediaStream;
        video.onloadedmetadata = (event) => {
            video.play();
        };
    } catch (err) {
        console.error(err.message);
    }
};


/**
* 
* Switch the selected camera from front to back or the reverse
* 
* @param {string} cameraMode 
*/
async function switchCamera(cameraMode) {

    // Switch camera modes
    if (!cameraMode) cameraMode = (camera_mode === 'environment') ? `user` : `environment`;

    try {
        // Stop the current video stream
        if (mediaStream != null && mediaStream.active) {
            var tracks = mediaStream.getVideoTracks();
            tracks.forEach(track => {
                track.stop();
            })
        }

        // Set the video source to null
        document.getElementById('cam').srcObject = null;

        // Change "facingMode"
        constraints.video.facingMode = cameraMode;
        camera_mode = cameraMode;

        // Get new media stream
        await getMediaStream(constraints);

    } catch (err) {
        console.error(err.message);
        alert(err.message);
    }
}


/**
* 
* Grab the current static image and add it as a blob to the current dropzone
* 
* @param {event} e 
*/
const usePhoto = (e) => {

    let $this = $(e.currentTarget);

    if (!window.blob) {
        Toast.fire({
            icon: 'error',
            title: 'Photo is not ready yet. Please try again.'
        });
        return false;
    }

    // Get the current customer ID
    let customer_id = $this.attr('data-customer-id');

    // Find the correct dropzone item
    let dropzone = window.dropzones.filter(dropzone => dropzone.id === customer_id)[0];

    if (!dropzone) {
        Toast.fire({
            icon: 'error',
            title: 'Upload area is not ready. Please refresh and try again.'
        });
        return false;
    }

    // Add the file to the dropzone which triggers an upload event
    dropzone.dropzone.addFile(window.blob);

    // Close the active modal
    webCamModal.close();

};

/**
* 
* Clear the current static image and reset the camera
* 
*/
const clearPicture = () => {

    // Get our canvas and its context
    let canvas = document.getElementById('canvas');
    let photo = document.getElementById('photo');
    let context = canvas.getContext('2d');

    // Clear the canvas
    context.fillStyle = "#aaa";
    context.fillRect(0, 0, canvas.width, canvas.height);

    // Replace the image with a clear colour
    let data = canvas.toDataURL('image/png');
    photo.setAttribute('src', data);
    photo.style.display = 'none';

    // Show and hide the associated buttons
    $('.js-delete-photo, .js-use-photo').hide();
    $('.js-switch-camera,.js-take-photo').show();
};


/**
* 
* Take a staic image using the currently selected camera
* 
* @param {event} e 
*/
const takePicture = (e) => {

    // Define our variables
    let canvas = document.getElementById('canvas');
    let video = document.getElementById('cam');
    let photo = document.getElementById('photo');
    let context = canvas.getContext('2d');
    const height = video.videoHeight;
    const width = video.videoWidth;

    // If there is an available image
    if (width && height) {
        canvas.width = width;
        canvas.height = height;
        context.drawImage(video, 0, 0, width, height);
        let data = canvas.toDataURL('image/png');

        // Convert the image to a blob
        canvas.toBlob((blob) => {
            window.blob = blob;
            window.blob.name = 'upload.jpeg';
        }, "image/jpeg", 0.95);

        // Update the src of the image
        photo.setAttribute('src', data);
        photo.style.display = 'block';

        // Show and hide the associated buttons
        $('.js-delete-photo, .js-use-photo').show();
        $('.js-switch-camera,.js-take-photo').hide();

    } else {
        clearphoto();
    }
};


/**
* 
* Open up the modal for rental agreement
* 
* @param {*} e 
*/
const openRentalAgreementModal = (e) => {

    // Prevent the defualt action
    e.preventDefault();

    const agreementUrl = $(e.currentTarget).attr('href');

    if (!agreementUrl || agreementUrl === 'null') {
        Toast.fire({
            icon: 'error',
            title: 'Rental agreement is not available yet.'
        });
        return false;
    }

    // Create a new tingle modal element
    window.rentalAgreementModal = new tingle.modal({
        cssClass: ['rentalAgreementModal'],
        onClose() {
            rentalAgreementModal.destroy();
        }
    });

    // Set the modal content
    rentalAgreementModal.setContent(templates.rental_agreement_modal(agreementUrl));

    // Open up the modal
    rentalAgreementModal.open();

    // Destroy the webcam instance
    $('.js-delete-photo, .js-use-photo').hide();
    $('.js-switch-camera,.js-take-photo').show();

};


/**
* 
* Open up the modal for the webcam
* 
* @param {*} e 
*/
const openWebcamModal = (e) => {

    // Prevent the defualt action
    e.preventDefault();

    // Create a new tingle modal element
    window.webCamModal = new tingle.modal({
        cssClass: ['videoModal'],
        onClose() {
            webCamModal.destroy();
        },
        onOpen() {
            // Create the webcam instance
            clearPicture();
            switchCamera('environment');
        }
    });

    // Set the modal content
    webCamModal.setContent(templates.webcam_modal($(e.currentTarget).attr('data-customer-id')));

    // Open up the modal
    webCamModal.open();

    // Destroy the webcam instance
    $('.js-delete-photo, .js-use-photo').hide();
    $('.js-switch-camera,.js-take-photo').show();

};


/**
* 
* Delete an upload associated to a driver
* 
* @param {event} e 
*/
const deleteUpload = (e) => {

    // Get the current element
    let $this = $(e.currentTarget);

    // Add a loading animation to the button
    $this.parent().addClass('busy');

    // Get the document id and reservation reference
    let document_id = parseInt($this.attr('data-upload-id'));
    let reservation_reference = window.portal_data.bookinginfo[0].reservationref;

    // Delete the upload and refetch the document list an dnot everything else 
    api.delete_upload({ reservation_reference, document_id: -Math.abs(document_id) })
        .then(response => {
            api.fetch_document_list({ reservation_reference })
                .then(json => {
                    window.portal_data.documentlinkdata = json.results;
                    populateLicenseTab();
                });
        });

};

/**
* 
* Remove an extra driver from a booking
* 
* @param {element} $element 
*/
const removeExtraDriver = ($element) => {

    // Add aloading animation to the button
    $element.addClass('busy');

    // Get the customer id from the element
    let customer_id = parseInt($element.attr('data-id'));

    // Get the driver data from the filtered portal data
    let driver_data = window.portal_data.extradrivers.filter(driver => driver.customerid === customer_id)[0];

    // Show a warning screen to the user so they can cancel the deletion
    Swal.fire({
        customClass: { confirmButton: 'button button--orange' },
        showCancelButton: true,
        denyButtonText: 'Cancel',
        confirmButtonText: 'Remove',
        title: 'Remove extra driver',
        html: `<p>Are you sure you want to delete this extra driver</p>`,
    }).then((result) => {

        // Only remove the driver if the user clicked confirm
        if (result.isConfirmed) {

            // Remove a users signature from the database by replacing it with a blank 1x1 pixel
            api.remove_signature({
                reservation_reference: window.portal_data.bookinginfo[0].reservationref,
                signature_template_id: 0,
                extra_driver_id: driver_data.customerid
            }).then(json => {

                // Remove the extra driver
                api.remove_extra_driver({
                    reservation_reference: window.portal_data.bookinginfo[0].reservationref,
                    customer_id: -driver_data.customerid,
                    first_name: driver_data.firstname,
                    last_name: driver_data.lastname,
                    email: driver_data.email,
                })
                    .then(json => {

                        // Fetch the updated data (there is no extra driver specific call)
                        api.fetch_workflow_checklist({ reservation_reference: window.portal_data.bookinginfo[0].reservationref })
                            .then(json => {
                                window.portal_data = json.results;
                                populateDriverTab();
                                populateDriverTab();
                                populateAddonTab();
                                populatePaymentsTab();
                                populateSignaturesTab();
                                populateLicenseTab();

                                // Remove the loading class to the button
                                $('.js-remove-extra-driver').removeClass('busy');

                                // Update the validity of the steps
                                update_step_validitiy();

                            });

                    }).catch(error => {
                        // Show the error
                        console.error(error);

                        // Log any errors discretely
                        log.error('API catch: remove_extra_driver', error);

                    });

            }).catch(error => {
                // Show the error
                console.error(error);

                // Log any errors discretely
                log.error('API catch: remove_signature', error);

            });

        } else {

            // Remove the loading class to the button
            $('.js-remove-extra-driver').removeClass('busy');

        }

    });

}


/**
* 
* Add to the input on an addon item
* 
* @param {object} $element 
*/
const subtractFromQuantity = ($element) => {

    if ($('.portalPage').length === 0) return;

    // Get our closest input
    let $input = $element.closest('.js-addon-quantity-wrap').find('.js-addon-quantity');

    // Find it's current value
    let currentValue = parseInt($input.val());

    // If the value is under a specified amount we can subtract from it
    if ($input.val() > 1) $input.val(currentValue - 1);

}


/**
* 
* Add to the input on an addon item
* 
* @param {object} $element 
*/
const addToQuantity = ($element) => {

    if ($('.portalPage').length === 0) return;

    // Get our closest input
    let $input = $element.closest('.js-addon-quantity-wrap').find('.js-addon-quantity');

    // Find it's current value
    let currentValue = parseInt($input.val());

    // If the value is under a specified amount we can add to it
    if ($input.val() < 100) $input.val(currentValue + 1);

}


/**
* 
* Hide items from a collection
* 
* @param {collection} collection 
* @param {array} hidden_items 
* @returns 
*/
const hideItems = (collection, hidden_items) => {

    // If there is a filtering array present
    if (hidden_items) collection = collection.filter((item) => hidden_items.indexOf(item.id) === -1);

    // Return the sorted & limited collection
    return collection;
}


/**
* 
*  Sort and limit a collection based on 2 external arrays
* 
* @param {collection} collection 
* @param {array} limit 
* @param {array} order 
* @returns 
*/
const sortAndLimit = (collection, limit, order, direction) => {

    if (order && order.length > 0) {
        collection = [...collection].sort((a, b) => {
            const indexA = order.indexOf(parseInt(a.id));
            const indexB = order.indexOf(parseInt(b.id));

            const sortIndexA = indexA === -1 ? 999 : indexA;
            const sortIndexB = indexB === -1 ? 999 : indexB;

            return direction === 'asc'
                ? sortIndexA - sortIndexB
                : sortIndexB - sortIndexA;
        });
    }

    if (limit) {
        collection = collection.slice(0, limit);
    }

    return collection;
};

const hideItemsHardcoded = (collection, booking_info) => {
    // Rule: if drop-off location ID is 60 or 8 then don't show extra fee ID 378
    if (booking_info.dropofflocationid) {
        const id = booking_info.dropofflocationid;
        if ((id === 60 || id === 8)) {
            console.log(`%c Hardcoded addon removal`, 'color: #86AFF5');
            collection = collection.filter(item => item.id !== 378);
        }
    }
    // Return the sorted & limited collection
    return collection;
}



/**
* 
*  Filter out insurance items that are not available for the current vehicle
* 
* @param {collection} collection 
* @param {array} limit 
* @param {array} order 
* @returns 
*/
const updatedSpecificInsuranceItems = (collection, selected_insurance_id, allowed_insurance_ids = []) => {

    // Hardcoded swtich statement from Martijn
    switch (selected_insurance_id) {
        case 346: allowed_insurance_ids = [346, 491, 501, 494, 498]; break;
        case 347: allowed_insurance_ids = [489, 347, 501, 494, 498]; break;
        case 353: allowed_insurance_ids = [489, 491, 501, 353, 498]; break;
        case 343: allowed_insurance_ids = [489, 491, 501, 494, 343]; break;
        case 345: allowed_insurance_ids = [345, 493, 500, 496, 497]; break;
        case 348: allowed_insurance_ids = [345, 493, 500, 496, 497]; break;
        case 352: allowed_insurance_ids = [488, 493, 500, 352, 497]; break;
        case 344: allowed_insurance_ids = [488, 493, 500, 494, 344]; break;
        case 355: allowed_insurance_ids = [355, 492, 502, 495, 499]; break;
        case 356: allowed_insurance_ids = [490, 356, 502, 495, 499]; break;
        case 354: allowed_insurance_ids = [490, 492, 502, 354, 499]; break;
        case 351: allowed_insurance_ids = [490, 492, 502, 495, 351]; break;
        default: allowed_insurance_ids = []; break;
    }

    // Filter out the insurance items that are not available for the current vehicle if it triggered the switch statement above
    if (allowed_insurance_ids.length) collection = collection.filter((item) => allowed_insurance_ids.indexOf(item.id) !== -1);

    // Return the filtered collection
    return collection;

}


/**
* 
* Enable/Disable the insurance items based on country rules
* 
*/
const allowInsurances = (portal_data) => {

    // If there is an agent present (i.e. this is an agent booking) and there is an insurance fee present
    if (portal_data.extrafees.find(fee => fee.isinsurancefee === true)) {

        // Find the selected insurance id
        let selected_insurance_id = window.portal_data.extrafees.find(fee => fee.isinsurancefee === true).extrafeeid;

        // Log the selected insurance id
        console.log(`%c Enable/Disable Insurances: ${selected_insurance_id}`, 'color: #60ab61');

        // Loop through the insurance items
        $('.insurances__item').each((index, item) => {

            // Australia specific insurance logic
            if (window.currency_name === 'NZD') {

                // Get the current item
                let $item = $(item);

                // Get the items' insurance ID
                let ID = parseInt($item.attr('data-id'));

                // If 410 or 409 is selected only show 402, 410, 409
                if (selected_insurance_id === 410) {
                    if (ID !== 410 && ID !== 402 && ID !== 436) {
                        $item.addClass('hidden');
                    }
                }

                // If 409 is selected only show 409, 402, 435
                if (selected_insurance_id === 409) {
                    if (ID !== 409 && ID !== 402 && ID !== 435) {
                        $item.addClass('hidden');
                    }
                }

                // If 402 or 435 or 436 is selected only show 402, 435, 436
                if (selected_insurance_id === 402 || selected_insurance_id === 435 || selected_insurance_id === 436) {
                    if (ID !== 402 && ID !== 435 && ID !== 436) {
                        $item.addClass('hidden');
                    }
                }

                if (portal_data.bookinginfo[0]?.agent !== null) {
                    const allowedMap = {
                        436: [436],
                        409: [409],
                        435: [436, 409, 435, 410],
                        410: [409, 435, 410, 435],
                    };

                    const allowed = allowedMap[selected_insurance_id];

                    if (allowed && !allowed.includes(ID)) {
                        $item.addClass('disabled');
                    }
                }

            }
            // Australia specific insurance logic
            if (window.currency_name === 'AUD') {

                // Get the current item
                let $item = $(item);

                // Get the items' insurance ID
                let ID = parseInt($item.attr('data-id'));

                if (selected_insurance_id === 183) {
                    if (ID !== 183 && ID !== 294 && ID !== 392) {
                        $item.addClass('hidden');
                    }
                }

                if (selected_insurance_id === 360) {
                    if (ID !== 360 && ID !== 294 && ID !== 391) {
                        $item.addClass('hidden');
                    }
                }

                if (selected_insurance_id === 294 || selected_insurance_id === 391 || selected_insurance_id === 392) {
                    if (ID !== 294 && ID !== 391 && ID !== 392) {
                        $item.addClass('hidden');
                    }
                }

                if (portal_data.bookinginfo[0]?.agent !== null) {
                    const allowedMap = {
                        360: [360],
                        392: [392],
                        391: [391, 360, 392, 183],
                        183: [183, 391, 360, 392],
                    };

                    const allowed = allowedMap[selected_insurance_id];

                    if (allowed && !allowed.includes(ID)) {
                        $item.addClass('disabled');
                    }
                }

            }

            // United Kingdonm specific insurance logic
            if (window.currency_name === 'GBP') {

                // Get the current item
                let $item = $(item);

                // Get the items' insurance ID
                let ID = parseInt($item.attr('data-id'));

                // CAMPERVAN LOGIC
                // Gold insurance
                if (selected_insurance_id === 494 || selected_insurance_id === 498) {
                    if (ID !== 494 && ID !== 498) {
                        $item.addClass('disabled');
                    }
                }

                // Silver insurance
                if (selected_insurance_id === 491 || selected_insurance_id === 501) {
                    if (ID !== 491 && ID !== 494 && ID !== 498 && ID !== 501) {
                        $item.addClass('disabled');
                    }
                }

                // MOTORHOME LOGIC
                // Gold insurance
                if (selected_insurance_id === 496 || selected_insurance_id === 497) {
                    if (ID !== 496 && ID !== 497) {
                        $item.addClass('disabled');
                    }
                }

                // Silver insurance
                if (selected_insurance_id === 493 || selected_insurance_id === 500) {
                    if (ID !== 493 && ID !== 496 && ID !== 497 && ID !== 500) {
                        $item.addClass('disabled');
                    }
                }

                // PEOPLE CARRIER LOGIC
                // Gold insurance
                if (selected_insurance_id === 495 || selected_insurance_id === 499) {
                    if (ID !== 495 && ID !== 499) {
                        $item.addClass('disabled');
                    }
                }

                // Silver insurance
                if (selected_insurance_id === 492 || selected_insurance_id === 502) {
                    if (ID !== 492 && ID !== 495 && ID !== 499 && ID !== 502) {
                        $item.addClass('disabled');
                    }
                }

            }


        });
    }

};


const setDefaultInsurance = (portal_data) => {

    if ($('.insurances__item.selected').length === 0) {

        let defaultInsurance = 402;
        if (window.location.origin === AU_PORTAL_SITE) defaultInsurance = 294;
        if (window.location.origin === UK_PORTAL_SITE) defaultInsurance = 349;
        if (window.location.origin === UK_PORTAL_SITE) defaultInsurance = 349;
        if (window.location.origin === UK_PORTAL_SITE && portal_data.bookinginfo[0].vehiclecategoryid === 22) defaultInsurance = 488;
        if (window.location.origin === UK_PORTAL_SITE && portal_data.bookinginfo[0].vehiclecategoryid === 16) defaultInsurance = 488;
        if (window.location.origin === UK_PORTAL_SITE && portal_data.bookinginfo[0].vehiclecategoryid === 25) defaultInsurance = 488;
        if (window.location.origin === UK_PORTAL_SITE && portal_data.bookinginfo[0].vehiclecategoryid === 26) defaultInsurance = 488;
        if (window.location.origin === UK_PORTAL_SITE && portal_data.bookinginfo[0].vehiclecategoryid === 1) defaultInsurance = 489;
        if (window.location.origin === UK_PORTAL_SITE && portal_data.bookinginfo[0].vehiclecategoryid === 12) defaultInsurance = 489;
        if (window.location.origin === UK_PORTAL_SITE && portal_data.bookinginfo[0].vehiclecategoryid === 14) defaultInsurance = 489;
        if (window.location.origin === UK_PORTAL_SITE && portal_data.bookinginfo[0].vehiclecategoryid === 17) defaultInsurance = 490;
        if (window.location.origin === UK_PORTAL_SITE && portal_data.bookinginfo[0].vehiclecategoryid === 18) defaultInsurance = 490;
        if (window.location.origin === UK_PORTAL_SITE && portal_data.bookinginfo[0].vehiclecategoryid === 19) defaultInsurance = 490;

        if ($(`.insurances__item[data-id="${defaultInsurance}"]`).length) {
            $(`.insurances__item[data-id="${defaultInsurance}"]`).addClass('selected');
        } else {
            $(`.insurances__item`).first().addClass('selected');
        }


    }
};


/**
* 
* Populate the addon tab
*  
*/
export const populateAddonTab = () => {

    // Simplified RCM variables
    let booking_info = window.portal_data.bookinginfo[0];

    // Hide intersecting items from a collection 
    // This is used on the insurance options to hide outdated items from the 2022 switch over
    let tidyInsurances = hideItems(window.portal_data.insuranceoptions, window.defaults.hidden_insurances)

    // Sort, limit and hide the insurance items
    tidyInsurances = sortAndLimit(tidyInsurances, 999, window.defaults.insurance_order, 'asc');

    // Filter out the insurance items that are not available for the current vehicle (hardcoded from Martijn)
    if (window.portal_data.extrafees.find(fee => fee.isinsurancefee === true)) tidyInsurances = updatedSpecificInsuranceItems(tidyInsurances, window.portal_data.extrafees.find(fee => fee.isinsurancefee === true).extrafeeid);

    // Set the default insurance item if any
    let mostPopularVehicle = window.vehicles.find(vehicle => parseInt(vehicle.VehicleID) === parseInt(booking_info.vehiclecategoryid));
    let mostPopular = (mostPopularVehicle) ? parseInt(mostPopularVehicle.DefaultInsurance) : undefined;

    let defaultSelected = undefined || undefined;

    // Append the insurance items to the DOM
    // $('.js-insurances-addons').html(tidyInsurances.map((item, index) => templates.insuranceItem(item, index, mostPopular, defaultSelected)).join(''));
    $('.js-insurances-addons').html(tidyInsurances.map((item, index) => templates.insuranceItemStacked(item, index, mostPopular, defaultSelected)).join(''));

    // Apply the match height to the insurance intro section
    MatchHeight.add('.insurances__header');

    // Enable/Disable the insurance items based on country rules
    allowInsurances(window.portal_data);

    // Set the default insurance if none is selected
    setDefaultInsurance(window.portal_data);

    // Sort and limit the addon items
    let tidyAddons;
    let addons = $('.js-car.selected').attr('data-addons-to-show')

    // If we have specific addons to show, then we should only show them
    if (addons && addons.length) {
        addons = addons.split(',').map(Number);
        tidyAddons = filterAndOrder(window.portal_data.optionalfees, addons, 'asc');
    } else {
        tidyAddons = sortAndLimit(window.portal_data.optionalfees, window.defaults.addon_limit, window.defaults.addon_order, 'desc');
    }

    tidyAddons = hideItemsHardcoded(tidyAddons, booking_info);

    // Sort by fees price when on the portal site TASK 31868929
    tidyAddons = tidyAddons.sort((a, b) => b.totalfeeamount - a.totalfeeamount);

    // Append the insurance items to the DOM
    $('.js-addons').html(tidyAddons.map(item => templates.addonItem(item)).join(''));

    // Apply the current selection
    let extra_fees = window.portal_data.extrafees;
    extra_fees.forEach((fee) => {
        if (fee.isinsurancefee !== true) {
            $(`.cards__item__addons__item[data-id="${fee.extrafeeid}"]`).addClass('added');
            $(`.cards__item__addons__item[data-id="${fee.extrafeeid}"]`).find('.js-addon-quantity').val(fee.qty);
        } else {
            $(`.insurances__item`).removeClass('selected');
            $(`.insurances__item[data-id="${fee.extrafeeid}"]`).addClass('selected');
        }
    });

    // Set the featured accessory item if any (attribute may be comma-separated, use the first one that isn't already selected or added)
    if (mostPopularVehicle.FeaturedAccessory) {
        let featuredAccessories = mostPopularVehicle.FeaturedAccessory.split(',').map(Number);

        for (const id of featuredAccessories) {
            let $item = $(`.cards__item__addons__item[data-id="${id}"]`).not('.selected, .added');

            if ($item.length) {
                $item.addClass('featured-accessory');
                break;
            }
        }
    }

}


/**
* 
* Change between the main driver and the extra drivers
* 
* @param {event} e 
*/
const changeInnerTab = (e) => {

    // Prevent the default action
    e.preventDefault();

    // Assign our variables
    let $this = $(e.currentTarget);
    let step = $this.attr('data-tab');

    // Add and remove the active classes to the tabs and triggers
    $('.active').removeClass('active');
    $(`[data-tab="${step}"]`).addClass('active');
};


/**
* 
* Disable the update button, usually if a user is on the signature step
* 
*/
const disableUpdateButton = () => {
    $('.js-open-booking-times-modal').addClass('disabled');
};


/**
* 
* Enable the update button, when a user is on any other step
* 
*/
const enableUpdateButton = () => {
    $('.js-open-booking-times-modal').removeClass('disabled');
};


/**
* 
* Change to a specific step in the portal.
* 
* @param {string} step 
*/
const change_steps = (step) => {

    let current_step = $('.js-change-step.current').attr('data-step') || '';


    if (step === 'previous') {
        if (current_step !== 'driver') {
            $('.js-change-step.current').prev('.js-change-step').trigger('click');

            // Vanilla smooth scroll to the top of the page
            setTimeout(() => window.scroll({ behavior: 'smooth', left: 0, top: 0 }), 400);

        }
    } else if (step === 'next') {
        if (current_step !== 'signature') {
            $('.js-change-step.current').next('.js-change-step').trigger('click');

            // Vanilla smooth scroll to the top of the page
            setTimeout(() => window.scroll({ behavior: 'smooth', left: 0, top: 0 }), 400);
        }
        else {
            $('.js-save-and-exit').trigger('click');
        }
    } else {
        $('[data-step].current').removeClass('current');
        $(`[data-step="${step}"]`).addClass('current');

        if (step === 'signature') {
            populateSignaturesTab();
        }

        if (step === 'signature') {
            populateSignaturesTab();
            disableUpdateButton();
        } else {
            enableUpdateButton();
        }
    }


    MatchHeight.update();

};

/**
* 
* Check all of the steps to see if they are valid or not
* 
*/
const update_step_validitiy = () => {

    // Driver Step Check
    if ($('.driverForm').is(':valid')) {
        $('.js-change-step[data-step="driver"]').addClass('valid');
        $('.js-change-step[data-step="driver"]').removeClass('invalid');
    } else {
        $('.js-change-step[data-step="driver"]').removeClass('valid');
        $('.js-change-step[data-step="driver"]').addClass('invalid');
    }

    // Addon Step Check
    if ($('.insurances__item.selected').length) {
        $('.js-change-step[data-step="addons"]').addClass('valid');
        $('.js-change-step[data-step="addons"]').removeClass('invalid');
    } else {
        $('.js-change-step[data-step="addons"]').removeClass('valid');
        $('.js-change-step[data-step="addons"]').addClass('invalid');
    }

    // License Step Check
    if (portal_data.documentlinkdata.every(license => license.isuploaded !== 0)) {
        $('.js-change-step[data-step="license"]').addClass('valid');
        $('.js-change-step[data-step="license"]').removeClass('invalid');
    } else {
        $('.js-change-step[data-step="license"]').removeClass('valid');
        $('.js-change-step[data-step="license"]').addClass('invalid');
    }

    // Payment Step Check
    if (portal_data.vaultlist && portal_data.vaultlist.length || portal_data.stripecarddata && portal_data.stripecarddata.length || portal_data.carddata && portal_data.carddata.length || portal_data.paymentinfo.length && !portal_data.paymentinfo.every(payment => payment.paymenttype === 'Agent Collected')) {
        $('.js-change-step[data-step="cards"]').addClass('valid');
        $('.js-change-step[data-step="cards"]').removeClass('invalid');
    } else {
        $('.js-change-step[data-step="cards"]').removeClass('valid');
        $('.js-change-step[data-step="cards"]').addClass('invalid');
    }

    // Signature Step Check 
    if (portal_data.signaturedata && portal_data.signaturedata.every(signature => signature.pngsig !== null)) {
        $('.js-change-step[data-step="signature"]').addClass('valid');
        $('.js-change-step[data-step="signature"]').removeClass('invalid');
    } else {
        $('.js-change-step[data-step="signature"]').removeClass('valid');
        $('.js-change-step[data-step="signature"]').addClass('invalid');
    }
};


/**
* 
* Check to see if a step is valid and whether or not the user 
* can proceed to the next step.
* 
* @param {string} step 
*/
const is_valid = (step, current_step) => {
    let valid = true;

    update_step_validitiy();

    if (current_step === 'driver') {
        if (!$('.driverForm').is(':valid')) {
            // Show success message to the user if the form is not valid
            Swal.fire({
                title: 'Warning',
                html: `<p>Please fill out all fields on this page first</p>`,
            })
            return false;
            valid = false;
        }
    }

    return valid;
};





/**
* 
* Populate the driver details tab
* 
*/
export const populateDriverTab = () => {

    // Simplified RCM variables
    let customer_info = window.portal_data.customerinfo[0];
    let booking_info = window.portal_data.bookinginfo[0];
    let countries = window.portal_data.countries;
    let extra_drivers = window.portal_data.extradrivers;

    $('.js-country-select').html(templates.countrySelect(countries));
    $('.js-license-country-input').html(templates.countrySelect(countries));
    $('.js-license-country-input-extra').html(templates.countrySelect(countries));

    // Driver details
    $('.js-first-name-input').val(customer_info.firstname);
    $('.js-last-name-input').val(customer_info.lastname);
    $('.js-email-input').val(customer_info.email);

    // Date of birth
    if (customer_info.dateofbirth !== '') {
        let date_of_birth = new dayjs(customer_info.dateofbirth, 'DD/MMM/YYYY');
        $('.js-date-of-birth-day-input').val(date_of_birth.format('DD'));
        $('.js-date-of-birth-month-input').val(date_of_birth.format('MM'));
        $('.js-date-of-birth-year-input').val(date_of_birth.format('YYYY'));
    }

    // Contact details
    $('.js-phone-input').val(customer_info.phone);
    $('.js-mobile-input').val(customer_info.mobile);
    $('.js-license-input').val(customer_info.licenseno);
    $('.js-license-country-input').val($('.js-license-country-input option').filter((index, element) => $(element).text() === customer_info.licenseissued).val());
    $('.js-travellers-input').val(booking_info.numbertravelling);

    // Expiration dates
    let license_expiration_date = new dayjs((customer_info.licenseexpires !== 'lifetime') ? customer_info.licenseexpires : '01/Jan/3000', 'DD/MMM/YYYY');
    if (customer_info.licenseexpires === 'lifetime') $('#no-expiration').prop('checked', true);

    if (customer_info.licenseexpires !== '' && license_expiration_date.isValid()) {
        $('.js-date-of-expiration-day-input').val(license_expiration_date.format('DD'));
        $('.js-date-of-expiration-month-input').val(license_expiration_date.format('MM'));
        $('.js-date-of-expiration-year-input').val(license_expiration_date.format('YYYY'));
    }

    // Address values
    $('.js-address-input').val(customer_info.address);
    $('.js-city-input').val(customer_info.city);
    $('.js-state-input').val(customer_info.state);
    $('.js-postcode-input').val(customer_info.postcode);
    $('.js-country-select').val($('.js-country-select option').filter((index, element) => $(element).text() === customer_info.country).val());

    // Remarks : Might need a way of filtering out non numbers and/or special characters?
    $('.js-remarks-input').val(booking_info.customerremark);

    // Specific test to see if a booking was created by an agent
    let isAgentBooking = (booking_info.agent && booking_info.agent !== '') ? true : false;

    // Add in the imagery for the vehicle
    $('.js-payment-image').html(templates.paymentThumbnail(booking_info.vehiclecategoryid));
    $('.js-vehicle-name').html(booking_info.vehiclecategory);
    $('.js-payment-dates').html(templates.paymentDates(window.portal_data));
    $('.js-payment-details').html(templates.paymentDetails(window.portal_data, isAgentBooking));

    // Render the extra drivers
    renderExtraDrivers(extra_drivers);

    // Update the validity of the steps
    update_step_validitiy();

    setTimeout(() => window.has_unsaved_changes = false, 800);

};


/**
* 
* Populate the saved cards tab using a fetched vault list
* 
* @param {array} vault_list 
*/
export const renderSavedCards = (vault_list = []) => {

    // Update the portal data with the new vault list
    window.portal_data.vaultlist = vault_list;

    // Empty the current cards list
    $('.js-cards').empty();

    // Loop through the vault list and append each card to the list
    vault_list.forEach(card => $('.js-cards').append(templates.card(card)));

    // Update the validity of the steps
    update_step_validitiy();

};


/**
* 
* Populate the payments tab by fetching the RCM vaultlist
* 
*/
export const populatePaymentsTab = () => {

    let supplier_id = 5;
    let reservation_reference = window.portal_data.bookinginfo[0].reservationref;

    // Update the payment link with the correct reservation reference
    $('.js-payment-link').attr('href', `${window.location.origin}/pay/?refID=${reservation_reference}&source=checkin`);

    // Fetch the vaultlist from the RCM API
    api.fetch_vault_list({ reservation_reference, supplier_id })
        .then(json => {
            (json.error === '') ? renderSavedCards(json.results) : console.error(json.error);;
        }).catch(error => {
            // Show the error
            console.error(error);

            // Log any errors discretely
            log.error('API catch: populatePaymentsTab', error);

        });
};


/**
* 
* Resize the signature canvas to match the container and high DPI screens
* 
* @param {Element} canvas 
* @param {object} signature_pad 
*/
export const resize_canvas = (canvas, signature_pad) => {
    let ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    canvas.getContext('2d').scale(ratio, ratio);
    signature_pad.clear();
};


/**
* 
* Add a signature and its respective interactions to the DOM
* 
* @param {nodeList} $signatures 
* @param {object} signature 
* @param {string} agreement_url 
*/
export const addSignature = ($signatures, signature, agreement_url) => {

    let $signature = $(templates.signature(signature, agreement_url));

    $signatures.append($signature);

    let canvas = $signature.find('canvas')[0];
    let reservation_reference = window.portal_data.bookinginfo[0].reservationref;
    let signature_pad = new SignaturePad(canvas);

    $signature.on('click', '.js-clear-signature', (e) => {

        let $this = $(e.currentTarget);

        // $this.addClass('busy');

        // Clear the signature pad
        signature_pad.clear();

        // Remove a users signature from the database by replacing it with a blank 1x1 pixel
        // api.remove_signature({
        //     reservation_reference,
        //     signature_template_id: signature.signaturetemplateid,
        //     extra_driver_id: (signature.customerid !== window.portal_data.customerinfo[0].customerid) ? signature.customerid : 0
        // }).then(json => {

        //     // Remove the loading state from the save button
        //     setTimeout(() => $this.removeClass('busy'), 500);

        //     // Re-render the newly saved signature
        //     (json.error === '') ? populateSignaturesTab(json.results) : console.error(json.error);

        // }).catch(error => {
        //     // Show the error
        //     console.error(error);

        //     // Log any errors discretely
        //     log.error('API catch: remove_signature', error);

        // });


    });

    $signature.on('click', '.js-save-signature', (e) => {

        let $this = $(e.currentTarget);

        let points = [];
        let signature_data = signature_pad.toData();

        if (signature_data.length) {
            signature_data.forEach((scribble) => {
                scribble.points.forEach((point) => {
                    points.push([parseFloat(point.x.toFixed(2)), parseFloat(point.y.toFixed(2))])
                });
            });

            $this.addClass('busy');
        } else {
            // Show success message to the user
            Toast.fire({
                icon: 'error',
                title: 'Please sign the signature pad'
            });
            return false;
        }

        api.save_signature({
            reservation_reference,
            signature_template_id: signature.signaturetemplateid,
            signature_json: { lines: [points] },
            extra_driver_id: (signature.customerid !== window.portal_data.customerinfo[0].customerid) ? signature.customerid : 0
        }).then(json => {

            // Send email if aus portal
            if (window.currency_name === 'AUD') {

                // Assign our variables
                let booking_info = window.portal_data.bookinginfo[0];

                // Send an email to the client on successful conversion
                api.send_email({
                    reservation_number: booking_info.reservationref,
                    email_type_name: 'checkinagreement',
                })
                    .then(json => {
                        // Log the end result
                        console.log(json);

                    }).catch(error => {

                        // Show the error
                        console.error(error);

                        // Log any errors discretely
                        log.error('API catch: send_email', error);

                    });

            }

            // Remove the loading state from the save button
            setTimeout(() => $this.removeClass('busy'), 500);

            // Re-render the newly saved signature
            (json.error === '') ? populateSignaturesTab(json.results) : console.error(json.error);

        }).catch(error => {
            // Show the error
            console.error(error);

            // Log any errors discretely
            log.error('API catch: save_signature', error);

        });

    });

    resize_canvas(canvas, signature_pad);

    if (signature.pngsig && signature.pngsig !== null) {
        signature_pad.fromDataURL(`data:image/png;base64,${signature.pngsig}`, { width: 387, height: 158 });
    }

}


/**
* 
* Render the extra drivers from an RCM extra driver list
* 
* @param {array} extra_drivers 
*/
export const renderExtraDrivers = (extra_drivers = []) => {
    let html = '';
    let $extraDrivers = $('.js-extra-drivers');

    // Empty the current extra drivers list
    $extraDrivers.empty();

    // Loop through the extra drivers and append each one to the list or show an empty list
    (extra_drivers.length) ? extra_drivers.forEach((extra_driver) => html += templates.extra_driver(extra_driver)) : html += templates.no_extra_drivers();

    // Set a limit on the number of extra drivers that can be added: currently set at 3 for Australia and 4 for other regions
    (extra_drivers.length >= (window.currency_name === 'AUD' ? 3 : 4)) ? $('.js-show-add-driver-form').hide() : $('.js-show-add-driver-form').show();

    // Update the extra drivers html
    $extraDrivers.html(html);
}


/**
* 
* Render the users signatures and signature pads from an RCM signature list
* 
* @param {array} signatures 
*/
export const renderSignatures = (signatures = []) => {

    let agreement_url = window.portal_data.bookinginfo[0].agreementurl;
    let $signatures = $('.js-signatures');

    // Empty the current signatures list
    $signatures.empty();

    // Loop through the signatures and append each one to the list or show an empty list
    if (signatures.length) {
        signatures.forEach((signature) => addSignature($signatures, signature, agreement_url));
    } else {
        addSignature($signatures, signatures, agreement_url);
    }

    // Update the validity of the steps
    update_step_validitiy();
}


/**
*
* Populate the signatures tab by fetching the RCM signature list
*  
*/
export const populateSignaturesTab = () => {

    let reservation_reference = window.portal_data.bookinginfo[0].reservationref;

    api.fetch_signature_list({ reservation_reference })
        .then(json => {
            (json.error === '') ? renderSignatures(json.results) : console.error(json.error);;
        }).catch(error => {

            // Show the error
            console.error(error);

            // Log any errors discretely
            log.error('API catch: populateSignaturesTab', error);

        });

};


export function createDropzones() {
    window.dropzones = [];

    $('.js-upload-empty').each((index, element) => {

        let $this = $(element);
        let timestamp = Date.now() / 1000 | 0;
        let dropzone = new Dropzone(element, dropzone_options);
        let reservation_reference = window.portal_data.bookinginfo[0].reservationref;

        dropzone.on('sending', function (file, xhr, formData) {
            formData.append('api_key', 128229988586749);
            formData.append('timestamp', timestamp);
            formData.append('callback', 'https://widget.cloudinary.com/cloudinary_cors.html');
            formData.append('upload_preset', 'hsfepzh0');
            formData.append('cloud_name', 'rentalcarmanager');
            formData.append('signature', window.signature);
        });

        dropzone.on('addedfile', async (file) => {
            const isHeic = file.type === 'image/heic' || file.type === 'image/heif' || file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif');

            if (isHeic) {
                $(dropzone.element).parent().addClass('busy');
                try {
                    const { default: heic2any } = await import('heic2any');
                    const jpegBlob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
                    const jpegFile = new File([jpegBlob], file.name.replace(/\.heic$/i, '.jpg').replace(/\.heif$/i, '.jpg'), { type: 'image/jpeg' });
                    jpegFile._heicConverted = true;
                    dropzone.removeFile(file);
                    dropzone.addFile(jpegFile);
                } catch (err) {
                    console.error('HEIC conversion failed', err);
                    $(dropzone.element).parent().removeClass('busy');
                    dropzone.removeAllFiles(true);
                    Toast.fire({ icon: 'error', title: 'Could not convert HEIC file. Please try a different format.' });
                }
                return;
            }

            setTimeout(() => {
                if (file.accepted) {
                    $(dropzone.element).parent().addClass('busy');
                    api.sign_upload({ timestamp: timestamp })
                        .then(data => {
                            if (!data.results || !data.results.signature) {
                                throw new Error(data.error || 'Unable to sign upload');
                            }

                            window.signature = data.results.signature;
                            dropzone.processQueue();
                            $(dropzone.element).parent().addClass('busy');
                        }).catch(error => {
                            console.error(error);
                            $(dropzone.element).parent().removeClass('busy');
                            dropzone.removeAllFiles(true);
                            Toast.fire({
                                icon: 'error',
                                title: 'Upload could not be started. Please try again.'
                            });
                        });
                } else {
                    $(dropzone.element).parent().removeClass('busy');
                    dropzone.removeAllFiles(true);
                    Toast.fire({
                        icon: 'error',
                        title: 'Could not upload that file, please try different one.'
                    });
                }
            }, 100)

        });

        dropzone.on('success', (file, responseText) => {
            api.store_upload({ reservation_reference, url: responseText.secure_url, customerid: $this.attr('data-customer-id') })
                .then(data => {
                    api.fetch_document_list({ reservation_reference })
                        .then(json => {
                            window.portal_data.documentlinkdata = json.results;
                            populateLicenseTab();
                        });
                });
        });

        dropzone.on('error', (file, message) => {
            setTimeout(() => {
                $(dropzone.element).parent().removeClass('busy');
                dropzone.removeFile(file);
            }, 2000)
            Toast.fire({
                icon: 'error',
                title: message || 'Upload failed. Please try again.'
            });
        });

        dropzones.push({ id: $this.attr('data-customer-id'), dropzone: dropzone });

    });

}


/**
*
* Populate the signatures tab by fetching the RCM signature list
*  
*/
export const populateLicenseTab = () => {

    let html = ``;
    let documents = window.portal_data.documentlinkdata;


    // Empty the current uploads list
    $('.js-uploads').empty();

    // Loop through the signatures and append each one to the list or show an empty item
    documents.forEach((document) => {
        if (document.isuploaded && document.documentlinkid) {
            html += templates.uploaded_document(document);
        } else {
            html += templates.upload_document(document);
        }

    });

    // Update the html of the uploads list
    $('.js-uploads').append(html);

    // Create the dropzones for each of our needed upload items
    createDropzones();

};


$(() => {

    /**
    * 
    * Toggle the expiration date on a driver form to simulate non expiring licenses
    * 
    * @param {event} e 
    */
    const toggleExpirationDate = (e) => {

        let $wrap = $(e.currentTarget).closest('.field');

        if ($(e.currentTarget).prop('checked') === true) {
            $wrap.find('input').eq(0).val('01');
            $wrap.find('input').eq(1).val('01');
            $wrap.find('input').eq(2).val('3000');
        } else {
            $wrap.find('input').eq(0).val(dayjs().format('DD'));
            $wrap.find('input').eq(1).val(dayjs().format('MM'));
            $wrap.find('input').eq(2).val(dayjs().format('YYYY'));
        }
    };


    /**
    * 
    * Show the add an extra driver form
    * 
    * @param {event} e 
    */
    const showAddDriverForm = (e) => {
        if (e) e.preventDefault();
        $('.extraDriverForm').show();
    };


    /**
    * 
    * Hide the add an extra driver form
    * 
    * @param {event} e 
    */
    const hideAddDriverForm = (e) => {
        if (e) e.preventDefault();
        $('.extraDriverForm').hide();
        $('.extraDriverForm')[0].reset();
    };


    /**
    * 
    * Save a users addons to their booking
    * This is a proxy function incase we need to add any other functionality
    * 
    * @param {event} e 
    */
    const saveAddons = (e) => {

        // Prevent the default action
        if (e) e.preventDefault();

        // Submit the main driver form
        submitDriverForm(e);
    };


    /**
    * 
    * Submit handler for the driver form to update a users booknig details
    * 
    * @param {event} e 
    */
    const submitDriverForm = (e) => {

        // Prevent the default action
        if (e) e.preventDefault();

        // Assign our variables
        let booking_info = window.portal_data.bookinginfo[0];

        // Add a loading class to the button
        $('.js-save-details,.js-save-addons').addClass('busy');

        // Get the date of birth and expiration dates
        let date_of_birth = new dayjs(`${$('.js-date-of-birth-day-input').val()}/${$('.js-date-of-birth-month-input').val()}/${$('.js-date-of-birth-year-input').val()}`, 'DD/MM/YYYY').format('DD/MMM/YYYY');
        let date_of_expiration = new dayjs(`${$('.js-date-of-expiration-day-input').val()}/${$('.js-date-of-expiration-month-input').val()}/${$('.js-date-of-expiration-year-input').val()}`, 'DD/MM/YYYY').format('DD/MMM/YYYY');

        // Check to see if the users birth date is valid
        if (date_of_birth === 'Invalid Date') {

            Swal.fire({
                title: 'Warning',
                html: `<p>Please enter a valid date of birth</p>`,
            });

            // Remove the loading class to the button
            $('.js-save-details,.js-save-addons').removeClass('busy');

            return false;
        }

        // Check to see if the users license expiration date is valid
        if (date_of_expiration === 'Invalid Date') {

            Swal.fire({
                title: 'Warning',
                html: `<p>Please enter a valid date of expiration</p>`,
            });

            // Remove the loading class to the button
            $('.js-save-details,.js-save-addons').removeClass('busy');

            return false;
        }

        // Special actions to override the insurances
        // 22 - motorhome
        // 16 - motorhome
        // 1 - campervan
        // 12 - campervan
        // 14 - campervan
        // 17 - people carrier
        let defaultInsurance = 402;
        if (window.location.origin === AU_PORTAL_SITE) defaultInsurance = 294;
        if (window.location.origin === UK_PORTAL_SITE) defaultInsurance = 349;
        if (window.location.origin === UK_PORTAL_SITE) defaultInsurance = 349;
        if (window.location.origin === UK_PORTAL_SITE && booking_info.vehiclecategoryid === 22) defaultInsurance = 488;
        if (window.location.origin === UK_PORTAL_SITE && booking_info.vehiclecategoryid === 16) defaultInsurance = 488;
        if (window.location.origin === UK_PORTAL_SITE && booking_info.vehiclecategoryid === 26) defaultInsurance = 488;
        if (window.location.origin === UK_PORTAL_SITE && booking_info.vehiclecategoryid === 25) defaultInsurance = 488;
        if (window.location.origin === UK_PORTAL_SITE && booking_info.vehiclecategoryid === 1) defaultInsurance = 489;
        if (window.location.origin === UK_PORTAL_SITE && booking_info.vehiclecategoryid === 12) defaultInsurance = 489;
        if (window.location.origin === UK_PORTAL_SITE && booking_info.vehiclecategoryid === 14) defaultInsurance = 489;
        if (window.location.origin === UK_PORTAL_SITE && booking_info.vehiclecategoryid === 17) defaultInsurance = 490;
        if (window.location.origin === UK_PORTAL_SITE && booking_info.vehiclecategoryid === 18) defaultInsurance = 490;
        if (window.location.origin === UK_PORTAL_SITE && booking_info.vehiclecategoryid === 19) defaultInsurance = 490;


        // Set the default insurance if none is selected
        setDefaultInsurance(window.portal_data);

        // Get our selected addons
        let optional_fees = getOptionalFees();

        // Get our selected addons
        let selected_insurance = getSelectedInsurance();

        // Add a prefix to the remarks field depending on the country
        let remarks_prefix = (window.currency_name === 'NZD') ? '' : 'Expected Travel Distance: ';

        // Remove the prefix if it already exists in the fields' value
        if ($('.js-remarks-input').val().includes('Show-Around: ')) remarks_prefix = '';
        if ($('.js-remarks-input').val().includes('Expected Travel Distance: ')) remarks_prefix = '';

        // Send our data to RCM
        api.edit_booking_portal({
            booking_type: 2,
            insurance_id: selected_insurance,
            number_of_people_travelling: parseInt($('.js-travellers-input').val()),
            reservation_reference: booking_info.reservationref,
            customer_id: window.portal_data.customerinfo[0].customerid,
            first_name: $('.js-first-name-input').val(),
            last_name: $('.js-last-name-input').val(),
            email: $('.js-email-input').val(),
            date_of_birth: date_of_birth,
            phone: $('.js-phone-input').val(),
            mobile: $('.js-mobile-input').val(),
            license_number: $('.js-license-input').val(),
            license_expiry: date_of_expiration,
            license_issue_country: $('.js-license-country-input').val(),
            address: $('.js-address-input').val(),
            city: $('.js-city-input').val(),
            state: $('.js-state-input').val(),
            postcode: $('.js-postcode-input').val(),
            country: $('.js-country-select').val(),
            optional_fees: optional_fees,
            remarks: `${remarks_prefix}${$('.js-remarks-input').val()}` || '',
        })
            .then(json => {

                if (json.status === 'ERR') {
                    error.showError(json.error);
                } else {
                    // Fetch the updated data as they do not return the needed information fromt he previous call
                    api.fetch_workflow_checklist({ reservation_reference: window.portal_data.bookinginfo[0].reservationref })
                        .then(json => {
                            window.portal_data = json.results;
                            populateDriverTab();
                            populateDriverTab();
                            populateAddonTab();
                            populatePaymentsTab();
                            populateSignaturesTab();
                            populateLicenseTab();

                            // Update the validity of the steps
                            update_step_validitiy();


                            // Remove the loading class to the button
                            $('.js-save-details,.js-save-addons').removeClass('busy');

                            // Show success message to the user
                            Toast.fire({
                                icon: 'success',
                                title: (window.first_load) ? 'Loaded your booking details' : 'You have successfully updated your details'
                            });

                            window.first_load = false;
                        });
                }
            }).catch(error => {
                // Show the error
                console.error(error);

                // Log any errors discretely
                log.error('API catch: make_booking', error);

            });

    }


    /**
    * 
    * Change between steps on the checkin portal
    * 
    * @param {event} e 
    */
    const changeStepsPreCheck = (e) => {

        // Prevent the default action
        e.preventDefault();

        // Assign our variables 
        let $this = $(e.currentTarget);
        let step = $this.attr('data-step');
        let current_step = $('.js-change-step.current').attr('data-step');

        // Check if the step is valid and proceed
        if (is_valid(step, current_step)) {
            if (window.has_unsaved_changes) {
                if (current_step === 'driver' || current_step === 'addons') {
                    // Submit the main driver form
                    submitDriverForm(e);
                }
            }
            change_steps(step);
        }
        // Show a bespoke error message when the step is not valid
        else {
            console.log('not valid')
        }
    };


    /**
    * 
    * Submit the extra driver form and show/hide animation classes
    * 
    * @param {event} e 
    */
    const submitExtraDriverForm = (e) => {

        e.preventDefault();

        // Add a loading class to the button
        $('.js-save-extra-driver-details').addClass('busy');

        let date_of_birth = new dayjs(`${$('.js-date-of-birth-day-input-extra').val()}/${$('.js-date-of-birth-month-input-extra').val()}/${$('.js-date-of-birth-year-input-extra').val()}`, 'DD/MM/YYYY').format('DD/MMM/YYYY');
        let date_of_expiration = new dayjs(`${$('.js-date-of-expiration-day-input-extra').val()}/${$('.js-date-of-expiration-month-input-extra').val()}/${$('.js-date-of-expiration-year-input-extra').val()}`, 'DD/MM/YYYY').format('DD/MMM/YYYY');

        api.add_extra_driver({
            reservation_reference: window.portal_data.bookinginfo[0].reservationref,
            customer_id: 0,
            first_name: $('.js-first-name-input-extra').val(),
            last_name: $('.js-last-name-input-extra').val(),
            email: $('.js-email-input-extra').val(),
            date_of_birth: date_of_birth,
            phone: $('.js-phone-input-extra').val(),
            license_number: $('.js-license-input-extra').val(),
            license_expiry: date_of_expiration,
            license_issue_country: $().val(),
            address: $().val(),
            state: $().val(),
            city: $().val(),
            country: $('.js-license-country-input-extra').val(),
            postcode: $().val(),
        })
            .then(json => {

                // Remove the loading class to the button
                $('.js-save-extra-driver-details').removeClass('busy');

                // Hide & reset the form
                hideAddDriverForm();

                // Assign the new portal data to its respective location
                window.portal_data.extradrivers = json.results.extradrivers;

                // Re-render outthe new extra drivers 
                renderExtraDrivers(window.portal_data.extradrivers);

            }).catch(error => {
                // Show the error
                console.error(error);

                // Log any errors discretely
                log.error('API catch: make_booking', error);

            });

    };


    /**
    * 
    * Select a specific addon item
    * 
    * @param {object} $element 
    */
    const selectPortalAddonItem = ($element) => {

        // Toggle the added class on the selected element
        $element.closest('.cards__item__addons__item').toggleClass('added');

        // An update has happend here so we have unsaved changes now that we need to deal with
        window.has_unsaved_changes = true;

    }

    /**
    * 
    * Select a specific insurance item
    * 
    * @param {object} $element 
    */
    const selectPortalInsuranceItem = ($element) => {

        // Do not do anything if they click on the currently selected insurance item
        if ($element.closest('.insurances__item').hasClass('selected')) return false;

        // Get both the current and selected insurance ID's
        let current_id = getSelectedInsurance();
        let selected_id = parseInt($element.closest('.insurances__item').data('id'));

        // Check if we need to show a message
        let should_show_message = false;
        let full_insurance_id;
        let has_changed_to_bronze = false;

        // New Zealand specific insurance logic
        if (window.currency_name === 'NZD') {
            if (current_id === 409) {
                if (selected_id === 435 || selected_id === 410 || selected_id === 402) {
                    should_show_message = true;
                    full_insurance_id = 409
                }
            }

            if (current_id === 410) {
                if (selected_id === 402) {
                    should_show_message = true;
                    full_insurance_id = 410
                }
            }

            if (current_id === 436) {
                if (selected_id === 435 || selected_id === 410 || selected_id === 402) {
                    should_show_message = true;
                    full_insurance_id = 436
                }
            }

            if (current_id === 435) {
                if (selected_id === 402) {
                    should_show_message = true;
                    full_insurance_id = 435
                }
            }


        }

        // Australia specific insurance logic
        if (window.currency_name === 'AUD') {
            if (current_id === 360 || current_id === 183) {
                if (selected_id === 294) {
                    should_show_message = true;
                    full_insurance_id = 360
                }
            }

            // (2025 updates)
            if (current_id === 392 || current_id === 391) {
                if (selected_id === 294) {
                    should_show_message = true;
                    full_insurance_id = 392
                }
            }
        }

        // United Kingdom specific insurance logic
        if (window.currency_name === 'GBP') {

            // ID's for motor homes
            if (selected_id === 488) {
                should_show_message = true;
                full_insurance_id = 497
            }


            // ID's for campervans
            if (selected_id === 489) {
                should_show_message = true;
                full_insurance_id = 498
            }

            // ID's people carriers
            if (selected_id === 490) {
                should_show_message = true;
                full_insurance_id = 499
            }

            // CAMPERVAN Silver => bronze
            if (current_id === 491 || current_id === 501) {
                if (selected_id === 489) {
                    should_show_message = true;
                    full_insurance_id = 491
                }
            }

            // MOTORHOME Silver => bronze
            if (current_id === 493 || current_id === 500) {
                if (selected_id === 488) {
                    should_show_message = true;
                    has_changed_to_bronze = true;
                    full_insurance_id = 493
                }
            }

            // PEOPLE CARRIER Silver => bronze
            if (current_id === 492 || current_id === 502) {
                if (selected_id === 490) {
                    should_show_message = true;
                    has_changed_to_bronze = true;
                    full_insurance_id = 492
                }
            }

            // CAMPERVAN Gold => Bronze
            if (current_id === 494 || current_id === 498) {
                if (selected_id === 489) {
                    should_show_message = true;
                    has_changed_to_bronze = true;
                    full_insurance_id = 491
                }
            }

            // MOTORHOME Gold => Bronze
            if (current_id === 496 || current_id === 497) {
                if (selected_id === 488) {
                    should_show_message = true;
                    has_changed_to_bronze = true;
                    full_insurance_id = 493
                }
            }

            // PEOPLE CARRIER Gold => Bronze
            if (current_id === 495 || current_id === 499) {
                if (selected_id === 490) {
                    should_show_message = true;
                    has_changed_to_bronze = true;
                    full_insurance_id = 492
                }
            }


        }

        // If we are on the booking page then we do not need to show a message and scare off customers
        if (window.pageType === 'book') should_show_message = false;

        // If we need to show a message, then show it
        if (should_show_message) {
            // This is a liability message for the insurance options if a user selects a less safe option
            // we then warn them about the potential risks
            let liability_message;

            if (window.currency_name === 'NZD') liability_message = /*html*/`<p>Car insurance isn't compulsory in New Zealand. Even if an accident isn't your fault you can still be liable for the damages if we can't recover the costs from the other party involved.</p><p>Only our Stress-Free insurance provides full peace of mind!</p>`;
            if (window.currency_name === 'AUD') liability_message = /*html*/`<p>Only our Stress-Free Insurance covers almost anything and has $0 excess.</p><p>On any other insurance level you are liable for the cost of the damages up to the excess amount.</p>`;
            if (window.currency_name === 'GBP') liability_message = /*html*/`<p>Only our Maximum Cover Insurance covers almost anything and has £0 excess.</p><p>On any other insurance level you are liable for the cost of the damages up to the excess amount.</p>`;

            if (has_changed_to_bronze) liability_message = /*html*/`<p>We recommend chosing our Silver Cover Insurance, which has low excess and covers heaps.</p>`;

            // Show a warning screen to the user so they can cancel the deletion
            Swal.fire({
                type: 'warning',
                showCancelButton: true,
                confirmButtonText: (has_changed_to_bronze) ? 'Yes, Silver Cover for me' : 'I want Stress-Free Insurance',
                cancelButtonText: 'No thanks, I\'ll take the risk',
                title: (window.currency_name === 'NZD') ? 'Please be aware...' : 'Are you sure?',
                html: liability_message,
            }).then((result) => {

                // Only remove the driver if the user clicked confirm
                if (result.isConfirmed) {

                    // Remove all of the selected classes from the other inurance elements
                    $('.insurances__item').removeClass('selected');

                    // Toggle the added class on the selected element
                    $(`.insurances__item[data-id="${full_insurance_id}"]`).addClass('selected');

                    // Submit the main driver form to save their selection
                    submitDriverForm();

                } else if (result.dismiss === 'cancel') {

                    // Remove all of the selected classes from the other inurance elements
                    $('.insurances__item').removeClass('selected');

                    // Toggle the added class on the selected element
                    $element.closest('.insurances__item').addClass('selected');

                    // Submit the main driver form to save their selection
                    submitDriverForm();
                }

            });

        }

        // Otherwise just change the selected insurance ID
        else {

            // An update has happend here so we have unsaved changes now that we need to deal with
            window.has_unsaved_changes = true;

            // Remove all of the selected classes from the other inurance elements
            $('.insurances__item').removeClass('selected');

            // Toggle the added class on the selected element
            $element.closest('.insurances__item').addClass('selected');
        }



    }

    /**
    * Toggle the visibliity of the feature set on a selected insurance or addon item
    * @param {object} $element 
    */
    const toggleFeatures = ($element) => {
        $element.closest('.insurances__item,.cards__item__addons__item').toggleClass('featuresVisible');
    }


    /**
    * 
    * Show the edit driver form and pre populate it with the driver data
    * 
    * @param {node} $element 
    */
    const editExtraDriver = ($element) => {

        // Get the customer id from the element
        let customer_id = parseInt($element.attr('data-id'));

        // Get the driver data from the filtered portal data
        let driver_data = window.portal_data.extradrivers.filter(driver => driver.customerid === customer_id)[0];

        // Show the add driver form
        showAddDriverForm();

        // Driver details
        $('.js-first-name-input-extra').val(driver_data.firstname);
        $('.js-last-name-input-extra').val(driver_data.lastname);
        $('.js-email-input-extra').val(driver_data.email);

        // Date of birth
        let date_of_birth = new dayjs(driver_data.dateofbirth, 'DD/MMM/YYYY');
        $('.js-date-of-birth-day-input-extra').val(date_of_birth.format('DD'));
        $('.js-date-of-birth-month-input-extra').val(date_of_birth.format('MM'));
        $('.js-date-of-birth-year-input-extra').val(date_of_birth.format('YYYY'));

        // Contact details
        $('.js-phone-input-extra').val(driver_data.phone);
        $('.js-license-input-extra').val(driver_data.licenseno);
        $('.js-license-country-input-extra').val(driver_data.countryid);

        // Expiration dates
        let license_expiration_date = new dayjs((driver_data.licenseexpires !== 'lifetime') ? driver_data.licenseexpires : '01/Jan/3000', 'DD/MMM/YYYY');
        if (driver_data.licenseexpires === 'lifetime') $('#no-expiration').prop('checked', true);
        $('.js-date-of-expiration-day-input-extra').val(license_expiration_date.format('DD'));
        $('.js-date-of-expiration-month-input-extra').val(license_expiration_date.format('MM'));
        $('.js-date-of-expiration-year-input-extra').val(license_expiration_date.format('YYYY'));

    }

    const saveAndContinue = (e) => {

        // Prevent the default action
        e.preventDefault();

        // Define our clicked button
        let $button = $(e.currentTarget);

        // Assign our variables
        let customer_info = window.portal_data.customerinfo[0];
        let booking_info = window.portal_data.bookinginfo[0];

        // Add a loading animation to the button
        $button.addClass('busy');

        let template_id, store_email;
        if (window.currency_name === 'NZD') {
            store_email = 'info@spaceshipsrentals.co.nz';
            template_id = 193;
        }
        if (window.currency_name === 'GBP') {
            store_email = 'info@spaceshipsrentals.co.uk';
            template_id = 202;
        }
        if (window.currency_name === 'AUD') {
            store_email = 'info@spaceshipsrentals.comm.au';
            template_id = 192;
        }

        api.send_external_email({
            template_id,
            first_name: customer_info.firstname,
            email: customer_info.email,
            store_email
        }).then(json => {

            Swal.fire({
                showCancelButton: false,
                confirmButtonText: 'Ok',
                title: 'Success',
                html: `<p>The check-in confirmation email has been sent to you.</p>`,
            }).then((result) => {

                // Only remove the driver if the user clicked confirm
                if (result.isConfirmed) {
                    window.location.reload();
                }

            });

        }).catch(error => {
            // Show the error
            console.error(error);

            // Log any errors discretely
            log.error('API catch: send_external_email', error);

        });


    };


    const saveAndExit = (e) => {

        // Prevent the default action
        e.preventDefault();

        // Define our clicked button
        let $button = $(e.currentTarget);

        // Add a loading animation to the button
        $button.addClass('busy');

        // Show a warning screen to the user so they can cancel the deletion
        Swal.fire({
            showCancelButton: false,
            allowEscapeKey: false,
            allowEnterKey: true,
            allowOutsideClick: false,
            confirmButtonText: 'Ok',
            title: 'Success',
            html: `<p>Your data has been saved.</p><p>You can always come back to the Online Check-in Portal to update your booking.</p>`,
        }).then((result) => {

            // Only remove the driver if the user clicked confirm
            if (result.isConfirmed) {
                window.location = window.location.origin + window.location.pathname;
            }

        });

    };


    /*------------------------------------------------------------------
    Event handlers
    ------------------------------------------------------------------*/

    // Show the add extra driver form
    $('body').on('click', '.js-show-add-driver-form', (e) => showAddDriverForm(e));

    // Hide & reset the add extra driver form
    $('body').on('click', '.js-hide-add-driver-form', (e) => hideAddDriverForm(e));

    // Toggle the expiration date fields for licenses
    $('body').on('change', '#no-expiration, #no-expiration-extra', (e) => toggleExpirationDate(e));

    // When a user submits the driver form
    $('body').on('submit', '.driverForm', (e) => submitDriverForm(e));

    // When a user interacts with the driver form input
    $('body').on('change', '.driverForm input, .driverForm select', (e) => window.has_unsaved_changes = true);

    // When a user submits the extra driver form
    $('body').on('submit', '.extraDriverForm', (e) => submitExtraDriverForm(e));

    // When a user selects an insurance item
    $('.portalPage').on('click', '.js-select-insurance', (e) => selectPortalInsuranceItem($(e.currentTarget)));

    // When a user selects an addon
    $('.portalPage').on('click', '.js-select-addon', (e) => selectPortalAddonItem($(e.currentTarget)));

    // When a user clicks on the save addons button
    $('body').on('click', '.js-save-addons', (e) => saveAddons(e));

    // When a user clicks on the add arrow on a number input
    $('body').on('click', '.js-add-quantity', (e) => addToQuantity($(e.currentTarget)));

    // When a user clicks on the add arrow on a number input
    $('body').on('click', '.js-subtract-quantity', (e) => subtractFromQuantity($(e.currentTarget)));

    // When a user clicks on the remove extra driver button
    $('body').on('click', '.js-remove-extra-driver', (e) => removeExtraDriver($(e.currentTarget)));

    // When a user clicks on the edit extra driver button
    $('body').on('click', '.js-edit-extra-driver', (e) => editExtraDriver($(e.currentTarget)));

    // When a user clicks on the change inner tab button
    $('body').on('click', '.js-change-inner-tab', (e) => changeInnerTab(e));

    // When a user clicks on the top of the page to cange the step they are on
    $('body').on('click', '.js-change-step', (e) => changeStepsPreCheck(e));

    // When a user clicks on an upload to delete it
    $('body').on('click', '.js-delete-upload', (e) => deleteUpload(e));

    // When a user clicks on the webcam button
    $('body').on('click', '.js-webcam', (e) => openWebcamModal(e));

    // When a user clicks on the rental agreement link
    $('body').on('click', '.js-rental-agreement-modal', (e) => openRentalAgreementModal(e));

    // When a user clicks on the change camera button
    $('body').on('click', '.js-switch-camera', (e) => switchCamera(e));

    // When a user clicks on the take photo button
    $('body').on('click', '.js-take-photo', (e) => takePicture(e));

    // When a user clicks on the delete photo button
    $('body').on('click', '.js-delete-photo', (e) => clearPicture(e));

    // When a user clicks on the use photo button
    $('body').on('click', '.js-use-photo', (e) => usePhoto(e));

    // When a user clicks on the close button on the webcam modal
    $('body').on('click', '.js-close-tingle', (e) => webCamModal.close());

    // When a user clicks on the close button on the webcam modal
    $('body').on('click', '.js-close-rental-agreement-tingle', (e) => rentalAgreementModal.close());

    // When a user clicks on the previous step button
    $('body').on('click', '.js-previous-step', () => change_steps('previous'));

    // When a user clicks on the next step button
    $('body').on('click', '.js-next-step', () => change_steps('next'));

    // When a user clicks on the update booking times button
    $('body').on('click', '.js-open-booking-times-modal', (e) => openBookingTimesModal($(e.currentTarget)));

    // When a user toggles a feature set
    $('body').on('click', '.js-toggle-features', (e) => toggleFeatures($(e.currentTarget)));

    // When a user clicks on save and continue
    $('body').on('click', '.js-save-and-continue', (e) => saveAndContinue(e));

    // When a user clicks on save and exit
    $('body').on('click', '.js-save-and-exit', (e) => saveAndExit(e));

    // When a user changes the date of birth day input
    $('body').on('change', '.js-date-of-birth-day-input,.js-date-of-birth-day-input-extra,.js-date-of-expiration-day-input,.js-date-of-expiration-day-input-extra', (e) => {
        if ($(e.currentTarget).val() > 31) $(e.currentTarget).val(31);
    });

    $('body').on('blur', '.js-date-of-birth-day-input,.js-date-of-birth-day-input-extra,.js-date-of-expiration-day-input,.js-date-of-expiration-day-input-extra', (e) => {
        // if single digit, prefix a 0
        if ($(e.currentTarget).val() > 0 && $(e.currentTarget).val() < 10 && $(e.currentTarget).val().length === 1) {
            $(e.currentTarget).val('0' + $(e.currentTarget).val());
        }
    });

    // When a user changes the date of birth month input
    $('body').on('change', '.js-date-of-birth-month-input,.js-date-of-birth-month-input-extra,.js-date-of-expiration-month-input,.js-date-of-expiration-month-input-extra', (e) => {
        if ($(e.currentTarget).val() > 12) $(e.currentTarget).val(12);
    });

    // When a user changes the date of birth month input
    $('body').on('blur', '.js-date-of-birth-month-input,.js-date-of-birth-month-input-extra,.js-date-of-expiration-month-input,.js-date-of-expiration-month-input-extra', (e) => {
        // if single digit, prefix a 0
        if ($(e.currentTarget).val() > 0 && $(e.currentTarget).val() < 10 && $(e.currentTarget).val().length === 1) {
            $(e.currentTarget).val('0' + $(e.currentTarget).val());
        }
    });


    // This is the default entry point for the portal to change to the correct tab on page load
    change_steps('driver');

    // Find the reservation reference from the URL if there is one
    let url = new URL(window.location.href);
    let pre_select_tab = url.searchParams.get('tab');

    // If there is a tab in the URL, change to that tab
    if (pre_select_tab) change_steps(pre_select_tab);

});
